import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getBookings, updateCompletionStatus } from "../../components/Api/GmsBooking";
import { getRooms } from "../../components/Api/GmsRoom";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { Calendar, Filter, Eye, Plus, Clock, User, MapPin } from "lucide-react";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const SpaCalendarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  
  // New state for popup modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateBookings, setDateBookings] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // New state for individual booking details modal
  const [showIndividualBookingModal, setShowIndividualBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaCalendar');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaCalendar');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaCalendar');
  const isAdmin = isUserAdmin();
  
  // Get current user's employee ID for filtering
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      // Ensure we start with the current month
      const currentDate = new Date();
      console.log('Initializing calendar with current date:', currentDate);
      setSelectedMonth(currentDate);
    }
  }, [hasPermission]);

  // Debug: Log the selected month when it changes
  useEffect(() => {
    console.log('Selected month changed:', selectedMonth);
    console.log('Current date:', new Date());
  }, [selectedMonth]);

  useEffect(() => {
    if (hasPermission) {
      loadBookings();
    }
  }, [selectedMonth, selectedCustomer, selectedEmployee, hasPermission]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCustomers(),
        loadEmployees(),
        loadSpaServices(),
        loadSpaPackages(),
        loadSpaPackageSeries(),
        loadMemberships(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({});
      const formattedCustomers = data.map(customer => ({
        value: customer.customer_Id,
        label: customer.customer_NameEn || customer.customer_Name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
      }));
      setEmployees(formattedEmployees);
      // Store employee data globally for entity name mapping
      window.employeeData = data;
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({});
      // Store services data for entity name mapping
      window.spaServicesData = data;
    } catch (error) {
      console.error('Error loading spa services:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadSpaPackages = async () => {
    try {
      const data = await getSpaPackages({});
      // Store packages data for entity name mapping
      window.spaPackagesData = data;
    } catch (error) {
      console.error('Error loading spa packages:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadSpaPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({});
      // Store package series data for entity name mapping
      window.spaPackageSeriesData = data;
    } catch (error) {
      console.error('Error loading spa package series:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadMemberships = async () => {
    try {
      const data = await getCustomerMemberships({});
      // Store memberships data for entity name mapping
      window.membershipsData = data;
    } catch (error) {
      console.error('Error loading memberships:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      
      // Get the first and last day of the selected month
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth();
      
      // Create dates in local timezone to avoid timezone issues
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Format dates as YYYY-MM-DD in local timezone
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const filterData = {
        from_Date: formatDate(firstDay),
        to_Date: formatDate(lastDay),
        customer_Id: selectedCustomer?.value || null,
        employee_Id: selectedEmployee?.value || null,
      };

      // Debug logging
      console.log('Calendar date range calculation:');
      console.log('Selected month:', selectedMonth);
      console.log('Year:', year, 'Month:', month);
      console.log('First day:', firstDay, '->', formatDate(firstDay));
      console.log('Last day:', lastDay, '->', formatDate(lastDay));

      // For view-only users, force filter by their employee ID
      if (isViewOnly && currentEmployeeId) {
        filterData.employee_Id = currentEmployeeId;
      }

      const data = await getBookings(filterData);
      setBookings(data || []);
      
      // Process calendar data
      processCalendarData(data || [], year, month);
    } catch (error) {
      console.error("Error loading bookings:", error);
      Swal.fire("Error", "Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  };

  const processCalendarData = (bookingsData, year, month) => {
    const calendar = {};
    
    // Initialize all days of the month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendar[dateKey] = 0;
    }

    // Count bookings for each date
    bookingsData.forEach(booking => {
      if (booking.booking_Date) {
        const bookingDate = new Date(booking.booking_Date);
        // Format the date to match our calendar keys
        const dateKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}-${String(bookingDate.getDate()).padStart(2, '0')}`;
        if (calendar[dateKey] !== undefined) {
          calendar[dateKey]++;
        }
      }
    });

    setCalendarData(calendar);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  const handleApplyFilter = () => {
    loadBookings();
  };

  const handleClearFilter = () => {
    setSelectedCustomer(null);
    setSelectedEmployee(null);
    const currentDate = new Date();
    console.log('Clearing filters, setting month to current date:', currentDate);
    setSelectedMonth(currentDate);
  };

  const handleAddBooking = (dateKey) => {
    // Navigate to booking page with the selected date
    navigate(`/GmsBooking?date=${dateKey}`);
  };

  const handleShowIndividualBooking = (booking) => {
    setSelectedBooking(booking);
    setShowIndividualBookingModal(true);
  };

  const handleDateClick = async (dateKey) => {
    try {
      setSelectedDate(dateKey);
      setModalLoading(true);
      setShowBookingModal(true);
      
      // Parse the clicked date correctly - use local date to avoid timezone issues
      const [yearStr, monthStr, dayStr] = dateKey.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr) - 1; // Month is 0-indexed
      const day = parseInt(dayStr);
      
      // Create date in local timezone and format as YYYY-MM-DD
      const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      console.log('=== DATE CLICK DEBUG ===');
      console.log('Clicked date:', dateKey);
      console.log('Year:', year, 'Month:', month, 'Day:', day);
      console.log('Formatted date for API:', formattedDate);
      console.log('Current date:', new Date());
      console.log('========================');
      
      const filterData = {
        from_Date: formattedDate,
        to_Date: formattedDate,
        customer_Id: selectedCustomer?.value || null,
        employee_Id: selectedEmployee?.value || null,
      };

      // For view-only users, force filter by their employee ID
      if (isViewOnly && currentEmployeeId) {
        filterData.employee_Id = currentEmployeeId;
      }

      console.log('API filter data:', filterData);

      const data = await getBookings(filterData);
      console.log('API response:', data);
      
      if (data && data.length > 0) {
        // Extract unique IDs from bookings
        const customerIds = [...new Set(data.map(b => b.customer_Id))];
        const employeeIds = [...new Set(data.map(b => b.employee_Id))];
        const roomIds = [...new Set(data.map(b => b.room_Id))];
        
        // Extract entity IDs for services, packages, and package series
        const serviceIds = [];
        const packageIds = [];
        const packageSeriesIds = [];
        
        data.forEach(booking => {
          if (booking.details) {
            booking.details.forEach(detail => {
              if (detail.entity_Type === 'Service') {
                serviceIds.push(detail.entity_Id);
              } else if (detail.entity_Type === 'Package') {
                packageIds.push(detail.entity_Id);
              } else if (detail.entity_Type === 'PackageSeries') {
                packageSeriesIds.push(detail.entity_Id);
              }
            });
          }
        });
        
        // Get names for all IDs using existing APIs
        const [customerData, employeeData, roomData] = await Promise.all([
          getCustomers({}),
          getEmployees({}),
          getRooms({})
        ]);
        
        // Use already loaded entity data or load if not available
        const serviceData = window.spaServicesData || [];
        const packageData = window.spaPackagesData || [];
        const packageSeriesData = window.spaPackageSeriesData || [];
        const membershipsData = window.membershipsData || [];
        
        // Entity data loaded successfully
        
        // Create lookup maps
        const customerMap = {};
        const employeeMap = {};
        const roomMap = {};
        const serviceMap = {};
        const packageMap = {};
        const packageSeriesMap = {};
        const membershipMap = {};
        
        customerData.forEach(c => customerMap[c.customer_Id] = c.customer_NameEn || c.customer_Name);
        employeeData.forEach(e => employeeMap[e.employee_Id] = e.employee_NameEn || e.employee_Name);
        roomData.forEach(r => roomMap[r.room_Id] = r.room_Name_English || r.room_Name);
        serviceData.forEach(s => serviceMap[s.spaService_Id] = s.spaServiceName_En || s.spaServiceName_Ar);
        packageData.forEach(p => packageMap[p.packageId] = p.packageName_En || p.packageName_Ar);
        packageSeriesData.forEach(ps => packageSeriesMap[ps.seriesId] = ps.seriesName_En || ps.seriesName_Ar);
        membershipsData.forEach(m => membershipMap[m.membershipId] = m);
        
        // Entity maps created successfully
        
        // Add names to bookings
        const enrichedBookings = data.map(booking => {
          // Get employee and room names from resource details
          const employeeIds = [...new Set(booking.details?.flatMap(d => d.resourceDetails?.map(r => r.employee_Id).filter(Boolean) || []) || [])];
          const roomIds = [...new Set(booking.details?.flatMap(d => d.resourceDetails?.map(r => r.room_Id).filter(Boolean) || []) || [])];
          
          // Employee mapping processed
          
          const employeeNames = employeeIds.map(id => employeeMap[id] || `Employee #${id}`).join(', ');
          const roomNames = roomIds.map(id => roomMap[id] || `Room #${id}`).join(', ');
          
          return {
            ...booking,
            customer_Name: customerMap[booking.customer_Id] || `Customer #${booking.customer_Id}`,
            employee_Name: employeeNames || 'No Employee',
            room_Name: roomNames || 'No Room',
            details: booking.details ? booking.details.map(detail => {
              let entityName = '';
              
              if (detail.entity_Type === 'Service' || detail.entity_Type === 'ser') {
                entityName = serviceMap[detail.entity_Id] || `Service #${detail.entity_Id}`;
              } else if (detail.entity_Type === 'Package' || detail.entity_Type === 'pac') {
                entityName = packageMap[detail.entity_Id] || `Package #${detail.entity_Id}`;
              } else if (detail.entity_Type === 'PackageSeries' || detail.entity_Type === 'sep') {
                entityName = packageSeriesMap[detail.entity_Id] || `Package Series #${detail.entity_Id}`;
              } else if (detail.entity_Type === 'mem') {
                // Handle membership using pre-loaded data
                const membership = membershipMap[detail.entity_Id];
                if (membership && membership.entity_Type === 'sep') {
                  // Get package series name
                  const seriesName = packageSeriesMap[membership.entity_Id] || `Package Series #${membership.entity_Id}`;
                  entityName = seriesName;
                } else if (membership && membership.entity_Type === 'pac') {
                  // Get package name
                  const packageName = packageMap[membership.entity_Id] || `Package #${membership.entity_Id}`;
                  entityName = packageName;
                } else {
                  entityName = `Membership #${detail.entity_Id}`;
                }
              } else {
                entityName = `Unknown #${detail.entity_Id}`;
              }
              
              return {
                ...detail,
                entity_Name: entityName
              };
            }) : []
          };
        });
        
        setDateBookings(enrichedBookings);
      } else {
        setDateBookings([]);
      }
    } catch (error) {
      console.error("Error loading date bookings:", error);
      Swal.fire("Error", "Failed to load booking details", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const formatTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      return "N/A";
    }
  };

  const getEntityTypeDisplay = (entityType) => {
    switch (entityType) {
      case 'Service': 
      case 'ser': return 'Spa Service';
      case 'Package': 
      case 'pac': return 'Spa Package';
      case 'PackageSeries': 
      case 'sep': return 'Package Series';
      case 'mem': return 'Membership';
      default: return entityType;
    }
  };

  const handleCompletionStatusUpdate = async (bookingResourceDetailId, newStatus, resourceDetail = null) => {
    try {
      // If resourceDetail is not provided, find it
      if (!resourceDetail) {
        // Check in dateBookings
        for (const booking of dateBookings) {
          if (booking.details) {
            for (const detail of booking.details) {
              if (detail.resourceDetails) {
                const found = detail.resourceDetails.find(r => r.bookingResourceDetail_Id === bookingResourceDetailId);
                if (found) {
                  resourceDetail = found;
                  break;
                }
              }
            }
          }
          if (resourceDetail) break;
        }
        
        // If not found in dateBookings, check in selectedBooking
        if (!resourceDetail && selectedBooking) {
          for (const detail of selectedBooking.details || []) {
            if (detail.resourceDetails) {
              const found = detail.resourceDetails.find(r => r.bookingResourceDetail_Id === bookingResourceDetailId);
              if (found) {
                resourceDetail = found;
                break;
              }
            }
          }
        }
      }
      
      // Validate that employee is assigned before allowing completion
      if (newStatus === 'Complete' && (!resourceDetail || !resourceDetail.employee_Id)) {
        Swal.fire({
          title: 'Cannot Mark as Done',
          text: 'Staff not assigned. Please assign an employee to this booking before marking it as complete.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      // Show confirmation dialog
      const actionText = newStatus === 'Complete' ? 'mark as done' : 'mark as pending';
      const confirmResult = await Swal.fire({
        title: 'Confirm Status Change',
        text: `Are you sure you want to ${actionText}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, confirm',
        cancelButtonText: 'Cancel'
      });
      
      if (!confirmResult.isConfirmed) {
        return; // User cancelled
      }
      
      const result = await updateCompletionStatus(bookingResourceDetailId, newStatus);
      
      if (result.isSuccess) {
        // Update the local state to reflect the change
        setDateBookings(prevBookings => 
          prevBookings.map(booking => ({
            ...booking,
            details: booking.details.map(detail => ({
              ...detail,
              resourceDetails: detail.resourceDetails.map(resource => 
                resource.bookingResourceDetail_Id === bookingResourceDetailId
                  ? { ...resource, completionStatus: newStatus }
                  : resource
              )
            }))
          }))
        );

        // Also update the selectedBooking state if it exists
        if (selectedBooking) {
          setSelectedBooking(prevBooking => ({
            ...prevBooking,
            details: prevBooking.details.map(detail => ({
              ...detail,
              resourceDetails: detail.resourceDetails.map(resource => 
                resource.bookingResourceDetail_Id === bookingResourceDetailId
                  ? { ...resource, completionStatus: newStatus }
                  : resource
              )
            }))
          }));
        }
        
        Swal.fire("Success", result.message, "success");
      } else {
        // Check if the error is about employee not found
        if (result.message && result.message.includes("Employee ID not found")) {
          Swal.fire({
            title: 'Cannot Mark as Done',
            text: 'Staff not assigned. Please assign an employee to this booking before marking it as complete.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
        } else {
          Swal.fire("Error", result.message, "error");
        }
      }
    } catch (error) {
      console.error("Error updating completion status:", error);
      // Check if the error response contains employee not found message
      const errorMessage = error.response?.data?.message || error.message || "Failed to update completion status";
      if (errorMessage.includes("Employee ID not found") || errorMessage.includes("Employee ID not found for this user")) {
        Swal.fire({
          title: 'Cannot Mark as Done',
          text: 'Staff not assigned. Please assign an employee to this booking before marking it as complete.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    }
  };

  const renderBookingModal = () => {
    if (!selectedDate) return null;

    const [year, month, day] = selectedDate.split('-');
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const displayDate = `${day} ${monthNames[parseInt(month) - 1]} ${year}`;

    return (
      <Modal 
        isOpen={showBookingModal} 
        toggle={() => setShowBookingModal(false)}
        size="lg"
        className="booking-details-modal"
      >
        <ModalHeader toggle={() => setShowBookingModal(false)}>
          <div className="d-flex align-items-center">
            <Calendar className="me-2" size={20} />
            <span>Bookings for {displayDate}</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {modalLoading ? (
            <div className="text-center py-4">
              <ClipLoader size={40} color="#556ee6" />
              <p className="mt-3">Loading booking details...</p>
            </div>
          ) : dateBookings.length === 0 ? (
            <div className="text-center py-4">
              <Calendar className="text-muted" size={48} />
              <h5 className="mt-3 text-muted">No Bookings</h5>
              <p className="text-muted">There are no bookings scheduled for this date.</p>
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <h6 className="text-primary mb-3">
                  <Clock className="me-2" size={16} />
                  Daily Schedule Overview
                </h6>
                <div className="schedule-timeline">
                  {dateBookings
                    .sort((a, b) => new Date(a.from_Time) - new Date(b.from_Time))
                    .map((booking, index) => (
                      <div key={index} className="schedule-item">
                        <div className="time-slot">
                          <span className="time">{formatTime(booking.from_Time)}</span>
                          <span className="separator">-</span>
                          <span className="time">{formatTime(booking.to_Time)}</span>
                        </div>
                        <div className="booking-info">
                          <div className="customer-name">
                            <User className="me-1" size={14} />
                            {booking.customer_Name}
                          </div>
                          <div className="employee-name">
                            <User className="me-1" size={14} />
                            {booking.employee_Name}
                          </div>
                          <div className="room-name">
                            <MapPin className="me-1" size={14} />
                            {booking.room_Name}
                          </div>
                          {/* Show completion status for current employee's services */}
                          {booking.details && booking.details.length > 0 && (
                            <div className="completion-status mt-2">
                              {booking.details.map((detail, detailIndex) => (
                                detail.resourceDetails && detail.resourceDetails.map((resource, resourceIndex) => {
                                  const canUpdate = isAdmin || (resource.employee_Id === currentEmployeeId);
                                  const isPending = resource.completionStatus === 'Pending';
                                  const hasEmployee = resource.employee_Id && resource.employee_Id !== null;
                                  
                                  if (!canUpdate) return null;
                                  
                                  return (
                                    <div key={`${detailIndex}-${resourceIndex}`} className="d-flex align-items-center gap-3 mb-2" style={{padding: '4px 0'}}>
                                      <div className="d-flex align-items-center gap-1">
                                        <span className="text-muted" style={{fontSize: '10px', fontWeight: '500'}}>Current Status:</span>
                                        <span className={`badge ${isPending ? 'bg-warning' : 'bg-success'} text-white`} style={{fontSize: '10px'}}>
                                          {resource.completionStatus || 'Pending'}
                                        </span>
                                      </div>
                                      <div className="vr" style={{height: '20px', opacity: 0.3}}></div>
                                      <Button
                                        size="sm"
                                        color={isPending ? 'success' : 'warning'}
                                        onClick={() => handleCompletionStatusUpdate(
                                          resource.bookingResourceDetail_Id, 
                                          isPending ? 'Complete' : 'Pending',
                                          resource
                                        )}
                                        className="btn-sm"
                                        style={{fontSize: '10px', padding: '2px 8px'}}
                                        disabled={isPending && !hasEmployee}
                                        title={isPending && !hasEmployee ? 'Not assigned' : `Mark as ${isPending ? 'Done' : 'Pending'}`}
                                      >
                                        Mark {isPending ? 'Done' : 'Pending'}
                                      </Button>
                                    </div>
                                  );
                                })
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="booking-actions">
                          <Button
                            color="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() => handleShowIndividualBooking(booking)}
                            title="View Details"
                          >
                            <Eye size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>


            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowBookingModal(false)}>
            Close
          </Button>
          {hasEditOrHigher && (
            <Button 
              color="primary" 
              onClick={() => navigate(`/GmsBooking?date=${selectedDate}`)}
            >
              <Plus className="me-1" size={16} />
              Add New Booking
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  };

  const renderCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const calendarDays = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      calendarDays.push(<td key={`empty-${i}`} className="text-muted"></td>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const bookingCount = calendarData[dateKey] || 0;
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      
      calendarDays.push(
        <td 
          key={day} 
          className={`text-center position-relative ${isToday ? 'bg-light' : ''} ${bookingCount > 0 ? 'cursor-pointer' : ''}`}
          onClick={() => bookingCount > 0 ? handleDateClick(dateKey) : null}
          style={{ cursor: bookingCount > 0 ? 'pointer' : 'default' }}
        >
          <div className="calendar-cell-content">
            <span className={`calendar-date ${isToday ? 'fw-bold text-primary' : ''}`}>{day}</span>
            <div className="calendar-controls">
              <div className="left-side">
                {bookingCount > 0 && (
                  <span 
                    className="badge bg-primary rounded-pill booking-count" 
                    title={`${bookingCount} booking(s) - Click to view details`}
                  >
                    {bookingCount}
                  </span>
                )}
              </div>
              {hasEditOrHigher && (
                <Button
                  color="outline-primary"
                  size="sm"
                  className="add-booking-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddBooking(dateKey);
                  }}
                  title={`Add booking for ${dateKey}`}
                >
                  <Plus size={10} />
                </Button>
              )}
            </div>
          </div>
        </td>
      );
    }

    // Create rows of 7 days each
    const rows = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      rows.push(
        <tr key={`row-${i}`}>
          {calendarDays.slice(i, i + 7)}
        </tr>
      );
    }

    return (
      <div className="calendar-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{monthNames[month]} {year}</h4>
          <div>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => handleMonthChange(new Date(year, month - 1))}
              className="me-2"
            >
              &lt; Previous
            </Button>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => handleMonthChange(new Date(year, month + 1))}
            >
              Next &gt;
            </Button>
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="table table-bordered calendar-table">
            <thead>
              <tr>
                {daysOfWeek.map(day => (
                  <th key={day} className="text-center bg-light">{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Calendar" breadcrumbItem="Spa" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <Calendar className="me-2" size={20} />
                    Spa Calendar
                  </h4>
                  {hasEditOrHigher && (
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="me-1" size={16} />
                      {showFilters ? 'Hide' : 'Show'} Filters
                    </Button>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                {/* View-only user message */}
                {isViewOnly && (
                  <div className="alert alert-info mb-3">
                    <i className="bx bx-info-circle me-2"></i>
                    You are viewing your own bookings only. Contact your administrator for additional access.
                  </div>
                )}

                {/* Filters Section - Only show for users with edit or higher permissions */}
                {hasEditOrHigher && showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label for="customer">Customer</Label>
                            <Select
                              id="customer"
                              value={selectedCustomer}
                              onChange={setSelectedCustomer}
                              options={customers}
                              isClearable
                              placeholder="Select Customer"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label for="employee">Employee</Label>
                            <Select
                              id="employee"
                              value={selectedEmployee}
                              onChange={setSelectedEmployee}
                              options={employees}
                              isClearable
                              placeholder="Select Employee"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label for="month">Month</Label>
                            <Input
                              id="month"
                              type="month"
                              value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
                              onChange={(e) => {
                                const [year, month] = e.target.value.split('-');
                                const newDate = new Date(parseInt(year), parseInt(month) - 1);
                                console.log('Month input changed:', e.target.value, '->', newDate);
                                handleMonthChange(newDate);
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                          <div className="d-flex gap-2">
                            <Button
                              color="primary"
                              onClick={handleApplyFilter}
                              disabled={loading}
                            >
                              {loading ? (
                                <ClipLoader size={16} color="#fff" />
                              ) : (
                                <>
                                  <Eye className="me-1" size={16} />
                                  Apply Filter
                                </>
                              )}
                            </Button>
                            <Button
                              color="outline-secondary"
                              onClick={handleClearFilter}
                              disabled={loading}
                            >
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Calendar Section */}
                <div className="calendar-wrapper">
                  {loading ? (
                    <div className="text-center py-5">
                      <ClipLoader size={50} color="#556ee6" />
                      <p className="mt-3">Loading calendar data...</p>
                    </div>
                  ) : (
                    renderCalendar()
                  )}
                </div>

                
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Booking Details Modal */}
      {renderBookingModal()}

      {/* Individual Booking Details Modal */}
      <Modal 
        isOpen={showIndividualBookingModal} 
        toggle={() => setShowIndividualBookingModal(false)}
        size="md"
        className="individual-booking-modal"
      >
        <ModalHeader toggle={() => setShowIndividualBookingModal(false)}>
          <div className="d-flex align-items-center">
            <Eye className="me-2" size={20} />
            <span>Booking Details</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedBooking && (
            <div>
              <div className="mb-4">
                <h6 className="text-primary mb-3">Time & Basic Info</h6>
                <div className="d-flex align-items-center mb-3">
                  <Clock className="me-2" size={16} />
                  <strong>{formatTime(selectedBooking.from_Time)} - {formatTime(selectedBooking.to_Time)}</strong>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <User className="me-2" size={16} />
                  <span><strong>Customer:</strong> {selectedBooking.customer_Name}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <User className="me-2" size={16} />
                  <span><strong>Employee:</strong> {selectedBooking.employee_Name}</span>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <MapPin className="me-2" size={16} />
                  <span><strong>Room:</strong> {selectedBooking.room_Name}</span>
                </div>
              </div>

              <div>
                <h6 className="text-primary mb-3">Services & Completion Status</h6>
                {selectedBooking.details && selectedBooking.details.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {selectedBooking.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="entity-group">
                        <h6 className="text-secondary mb-2">{getEntityTypeDisplay(detail.entity_Type)}: {detail.entity_Name}</h6>
                        {detail.resourceDetails && detail.resourceDetails.length > 0 ? (
                          <div className="resource-list">
                            {detail.resourceDetails.map((resource, resourceIndex) => {
                              // Check if current user is the assigned employee or admin
                              const canUpdate = isAdmin || (resource.employee_Id === currentEmployeeId);
                              const isPending = resource.completionStatus === 'Pending';
                              
                              return (
                                <div key={resourceIndex} className="resource-item">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center mb-1">
                                        <User className="me-2" size={14} />
                                        <span className="text-muted small">
                                          Employee: {
                                            !resource.employee_Id ? (
                                              <span style={{ color: '#dc3545', fontWeight: '500' }}>Not assigned</span>
                                            ) : (() => {
                                              // Get employee name from the global employee map
                                              const employeeMap = {};
                                              if (window.employeeData) {
                                                window.employeeData.forEach(e => employeeMap[e.employee_Id] = e.employee_NameEn || e.employee_Name);
                                              }
                                              return employeeMap[resource.employee_Id] || `Employee #${resource.employee_Id}`;
                                            })()
                                          }
                                        </span>
                                      </div>
                                      <div className="d-flex align-items-center mb-1">
                                        <Clock className="me-2" size={14} />
                                        <span className="text-muted small">
                                          {formatTime(resource.from_Time)} - {formatTime(resource.to_Time)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted small" style={{fontWeight: '500'}}>Current Status:</span>
                                        <span className={`badge ${isPending ? 'bg-warning' : 'bg-success'} text-white`}>
                                          {resource.completionStatus || 'Pending'}
                                        </span>
                                      </div>
                                      {canUpdate && (
                                        <>
                                          <div className="vr" style={{height: '24px', opacity: 0.3}}></div>
                                          <Button
                                            size="sm"
                                            color={isPending ? 'success' : 'warning'}
                                            onClick={() => handleCompletionStatusUpdate(
                                              resource.bookingResourceDetail_Id, 
                                              isPending ? 'Complete' : 'Pending',
                                              resource
                                            )}
                                            className="btn-sm"
                                            disabled={isPending && !resource.employee_Id}
                                            title={isPending && !resource.employee_Id ? 'Not assigned' : `Mark as ${isPending ? 'Done' : 'Pending'}`}
                                          >
                                            Mark {isPending ? 'Done' : 'Pending'}
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted small">No resource details</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">No details</span>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowIndividualBookingModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .calendar-table {
          background: white;
        }
        
        .calendar-table td {
          height: 100px;
          vertical-align: top;
          padding: 8px;
        }

        .calendar-date {
          font-size: 1.2rem;
          font-weight: 500;
          margin-bottom: 8px;
          display: block;
        }

        .calendar-cell-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .calendar-controls {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          width: 100%;
        }

        .left-side {
          min-width: 20px;
        }

        .booking-count {
          font-size: 0.65rem;
          padding: 2px 6px;
        }

        .add-booking-btn {
          padding: 2px 4px;
          min-width: 20px;
          height: 20px;
          font-size: 10px;
        }
        
        .calendar-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          padding: 12px 8px;
        }
        
        .calendar-container {
          max-width: 100%;
          overflow-x: auto;
        }
        
        .calendar-wrapper {
          min-height: 400px;
        }

        .schedule-timeline {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .schedule-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #556ee6;
        }

        .time-slot {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 120px;
          padding: 0.5rem;
          background-color: white;
          border-radius: 6px;
          border: 1px solid #dee2e6;
        }

        .time-slot .time {
          font-weight: 600;
          color: #556ee6;
        }

        .time-slot .separator {
          color: #6c757d;
          margin: 2px 0;
        }

        .booking-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .customer-name, .employee-name, .room-name {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
        }

        .customer-name {
          font-weight: 600;
          color: #495057;
        }

        .employee-name {
          color: #6c757d;
        }

        .room-name {
          color: #6c757d;
        }

        .booking-details-modal .modal-lg {
          max-width: 900px;
        }

        .entity-item {
          padding: 8px 12px;
          background-color: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #556ee6;
          margin-bottom: 4px;
        }

        .entity-item:last-child {
          margin-bottom: 0;
        }

        .entity-item small {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .entity-item strong {
          font-size: 0.9rem;
          line-height: 1.2;
        }

        .entity-group {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 16px;
          background-color: #f8f9fa;
        }

        .entity-group h6 {
          font-size: 0.9rem;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #556ee6;
        }

        .entity-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .entity-list .entity-item {
          margin-bottom: 0;
          background-color: white;
          border-left: 3px solid #556ee6;
        }

        .resource-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .resource-item {
          padding: 12px;
          background-color: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #dee2e6;
        }

        .resource-item:hover {
          background-color: #e9ecef;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .cursor-pointer:hover {
          background-color: #f8f9fa;
        }

        .booking-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
        }

        .booking-actions .btn {
          transition: all 0.2s ease;
        }

        .booking-actions .btn:hover {
          transform: scale(1.1);
        }
      `}</style>

    </div>
  );
};

export default SpaCalendarPage;
